import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { createReport, REPORT_REASONS, getReportReasonLabel } from "../../services/reportService";

/**
 * targetId    - id of the thing being reported
 * targetType  - "post" | "user" | "comment" | "draft"  (defaults to "post" for back-compat)
 * onClose     - called to dismiss the modal (no success message)
 * onSuccess   - called with the API success message after a successful submit;
 *               parent closes the modal AND displays the message
 */
const ReportModal = ({ targetId, targetType = "post", onClose, onSuccess }) => {
  const { apiCall } = useAuth();
  const [submittingReason, setSubmittingReason] = useState(null);
  const [error, setError] = useState(null);

  const handleOverlayClick = () => {
    if (!submittingReason) onClose();
  };

  const handleReasonClick = async (reasonId) => {
    if (submittingReason) return;

    setSubmittingReason(reasonId);
    setError(null);

    const result = await createReport(
      { target_type: targetType, target_id: targetId, reason: reasonId },
      apiCall
    );

    if (!result.success) {
      setError(result.error);
      setSubmittingReason(null);
      return;
    }

    onSuccess(result.message);
  };

  const title =
    targetType === "user"
      ? "Why are you reporting this account?"
      : "Why are you reporting this post?";

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center" onClick={(e) => e.stopPropagation()} >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={handleOverlayClick}
      />

      <div className="relative w-full bg-white dark:bg-gray-900 rounded-t-2xl max-h-[60vh] flex flex-col animate-slide-up sm:max-w-xl sm:mx-auto">
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0 text-center">
          <h3 className="text-base font-semibold text-black dark:text-white">
            {title}
          </h3>
        </div>

        <div className="flex-1 overflow-y-auto px-4">
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-xs text-red-600 font-medium">{error}</p>
            </div>
          )}

          {REPORT_REASONS.map((reasonId) => (
            <button
              key={reasonId}
              type="button"
              onClick={() => handleReasonClick(reasonId)}
              disabled={!!submittingReason}
              className="w-full text-left py-4 border-b border-gray-200 dark:border-gray-800 text-[15px] text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submittingReason === reasonId ? "Submitting..." : getReportReasonLabel(reasonId)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportModal;